"use client"

import { Slot } from "@radix-ui/react-slot"
import React, { useEffect, useRef, useState } from "react"
import { useIsMobile } from "../../hooks/use-mobile"
import { HoverCard, HoverCardContent, HoverCardTrigger } from "../shadcn/hover-card"

/**
 * @description 호버로 열리는 메뉴. TMenu와 동일한 API지만 hover 트리거. 모바일에서는 클릭으로 토글.
 * `clickToPin` 을 켜면 hover 프리뷰에 더해 트리거 클릭 시 열린 채 고정된다(재클릭·바깥클릭·Esc 로 닫힘).
 * @example
 * <THoverMenu
 *   renderButton={({ open }) => <TButton>메뉴</TButton>}
 *   renderMenu={({ onClose }) => (
 *     <div>
 *       <TMenuItem label="항목 1" onClick={onClose} />
 *     </div>
 *   )}
 * />
 */
interface THoverMenuProps {
	/** 메뉴 트리거 버튼 렌더 함수 (open 상태 전달) */
	renderButton: (props: { open?: boolean }) => React.ReactNode
	/** 메뉴 콘텐츠 렌더 함수 (onClose 콜백 전달) */
	renderMenu: (props: { onClose: () => void }) => React.ReactNode
	/** 외부 제어 열림 상태 */
	open?: boolean
	/**
	 * 클릭-고정(pin) 모드. 기본 hover 전용 동작에 더해, 트리거를 **클릭하면 열린 채 고정**된다
	 * (마우스가 떠나도 유지 — 재클릭·바깥클릭·Esc 로만 닫힘). hover 만으론 조작이 불편한 곳
	 * (좌하단 LNB 사용자 셀 등)에서 옵트인한다. 기본 false 라 기존 소비처 동작은 완전히 불변.
	 * @default false
	 */
	clickToPin?: boolean
	/**
	 * 메뉴 정렬
	 * @default "start"
	 */
	align?: "start" | "center" | "end"
	/**
	 * 메뉴 표시 방향
	 * @default "bottom"
	 */
	side?: "top" | "right" | "bottom" | "left"
	/** 정렬 오프셋 (px) */
	alignOffset?: number
	/** 방향 오프셋 (px) */
	sideOffset?: number
	/** 추가 CSS 클래스 */
	className?: string
	/** 메뉴 닫힘 콜백 */
	onMenuClose?: () => void
	/** 메뉴 열림 콜백 */
	onMenuOpen?: () => void
}

export const THoverMenu = React.memo((props: THoverMenuProps) => {
	const { renderButton, renderMenu, open, clickToPin = false, align = "start", side = "bottom", alignOffset = 0, sideOffset = 4, className, onMenuClose, onMenuOpen } = props

	const isMobile = useIsMobile()
	const [isOpen, setIsOpen] = useState(false)
	const [isHoverOpen, setIsHoverOpen] = useState(false)
	const [isClickLocked, setIsClickLocked] = useState(false)
	const isClickingRef = useRef(false)
	const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null)
	// clickToPin 고정 여부 — onOpenChange·바깥클릭·Esc 핸들러가 동기로 읽어야 해 state 가 아닌 ref.
	const isPinnedRef = useRef(false)

	// unmount 시 closeTimeoutRef cleanup — 미정리 시 unmount 후 setIsOpen 호출로 React warning + 메모리 누수.
	useEffect(() => {
		return () => {
			if (closeTimeoutRef.current) {
				clearTimeout(closeTimeoutRef.current)
				closeTimeoutRef.current = null
			}
		}
	}, [])

	const handleOpen = React.useCallback(() => {
		setIsOpen(true)
		onMenuOpen?.()
	}, [onMenuOpen])

	const handleClose = React.useCallback(() => {
		if (closeTimeoutRef.current) {
			clearTimeout(closeTimeoutRef.current)
			closeTimeoutRef.current = null
		}
		setIsOpen(false)
		setIsHoverOpen(false)
		setIsClickLocked(false)
		isPinnedRef.current = false
		onMenuClose?.()
	}, [onMenuClose])

	const handleTriggerClick = React.useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault()
			e.stopPropagation()

			// 모바일에서는 클릭으로 토글
			if (isMobile) {
				if (isOpen) {
					handleClose()
				} else {
					handleOpen()
				}
				return
			}

			// 클릭-고정 모드: 클릭 = 고정 토글. 고정 중이면 해제+닫기, 아니면 고정+열기(hover-out 무시로 계속 열림).
			if (clickToPin) {
				if (isPinnedRef.current) {
					isPinnedRef.current = false
					handleClose()
				} else {
					isPinnedRef.current = true
					if (!isOpen) {
						handleOpen()
					}
				}

				setTimeout(() => {
					isClickingRef.current = false
				}, 200)
				return
			}

			if (isHoverOpen || isOpen) {
				setIsClickLocked(true)
				setIsHoverOpen(true)

				if (!isOpen) {
					handleOpen()
				}

				setTimeout(() => {
					isClickingRef.current = false
				}, 200)
				return
			}

			setIsHoverOpen(false)
			setIsClickLocked(false)
			handleOpen()

			setTimeout(() => {
				isClickingRef.current = false
			}, 200)
		},
		[isMobile, clickToPin, isHoverOpen, isOpen, handleOpen, handleClose]
	)

	const onOpenChange = React.useCallback(
		(open: boolean) => {
			// 모바일에서는 hover 이벤트 무시 (클릭으로만 제어)
			if (isMobile) return

			if (isClickingRef.current) {
				return
			}

			// clickToPin 고정 중에는 hover-out(open=false)을 무시 — 마우스가 떠나도 열린 채 유지.
			// 닫기는 재클릭·바깥클릭·Esc 전담(handleTriggerClick / handlePointerDownOutside / handleEscapeKeyDown).
			if (clickToPin && isPinnedRef.current && !open) {
				return
			}

			if (isClickLocked && !open) {
				setIsClickLocked(false)
			}

			if (open) {
				if (closeTimeoutRef.current) {
					clearTimeout(closeTimeoutRef.current)
					closeTimeoutRef.current = null
				}
				setIsHoverOpen(true)
				handleOpen()
			} else {
				closeTimeoutRef.current = setTimeout(() => {
					handleClose()
					closeTimeoutRef.current = null
				}, 100)
			}
		},
		[isMobile, clickToPin, isClickLocked, handleOpen, handleClose]
	)

	const handleMouseDown = React.useCallback(() => {
		isClickingRef.current = true

		if (closeTimeoutRef.current) {
			clearTimeout(closeTimeoutRef.current)
			closeTimeoutRef.current = null
		}
	}, [])

	// clickToPin 전용 닫기 트리거 — Radix HoverCardContent(DismissableLayer)가 발화한다.
	// Esc: 즉시 닫기(고정 해제). Radix 기본 dismiss 도 함께 돌지만 handleClose 가 이미 닫아 무해하다.
	const handleEscapeKeyDown = React.useCallback(() => {
		handleClose()
	}, [handleClose])

	// 바깥 pointerdown: 트리거 위 클릭은 handleTriggerClick 이 토글을 담당하므로 제외한다
	// (HoverCard 는 트리거를 dismiss 브랜치로 등록하지 않아 트리거 클릭도 "바깥"으로 잡힌다 →
	//  preventDefault 로 Radix 기본 dismiss 를 막지 않으면 닫힘→재열림 경합이 난다).
	const handlePointerDownOutside = React.useCallback(
		(event: CustomEvent<{ originalEvent: PointerEvent }>) => {
			const target = event.detail.originalEvent.target
			if (target instanceof Element && target.closest('[data-slot="hover-card-trigger"]')) {
				event.preventDefault()
				return
			}
			handleClose()
		},
		[handleClose]
	)

	return (
		<HoverCard open={open ?? isOpen} onOpenChange={onOpenChange} openDelay={100} closeDelay={100}>
			<HoverCardTrigger asChild>
				{/* Radix Slot 직접 사용 — wrap element (div/span) 추가하지 않고 자식 (renderButton 결과 button) 자체가 trigger.
				    이전 div/span wrap 은 React 19 SSR 환경에서 hydration mismatch 회귀 (server: button trigger, client: wrap trigger).
				    Slot 은 자식 props 에 onClick/onMouseDown merge — server/client 모두 button trigger 로 동일. */}
				<Slot onClick={handleTriggerClick} onMouseDown={handleMouseDown}>
					{renderButton({ open: open ?? isOpen })}
				</Slot>
			</HoverCardTrigger>
			<HoverCardContent
				align={align}
				side={side}
				alignOffset={alignOffset}
				sideOffset={sideOffset}
				className={className}
				// clickToPin 일 때만 Esc·바깥클릭 닫기를 붙인다. undefined 면 Radix 기본과 동일해 기존 소비처 동작 불변.
				onEscapeKeyDown={clickToPin ? handleEscapeKeyDown : undefined}
				onPointerDownOutside={clickToPin ? handlePointerDownOutside : undefined}
			>
				{renderMenu({ onClose: handleClose })}
			</HoverCardContent>
		</HoverCard>
	)
})

THoverMenu.displayName = "THoverMenu"
